import ICON_FILES from '../config/iconFiles.json';
import { ARTWORK_VERSION, checkArtwork, iconFileName } from '../config/iconArtwork';
import { checkPack } from '../config/iconPacks';
import { PACK_MIN_NAMES } from '../config/iconPicker';

/**
 * The library icons' names and the session's cache of their artwork
 * (KPR-1187, KPE-037 "icons on demand"). components/common/LibraryIcon.js
 * draws from it and installs the loader that fetches a file; this module holds
 * no network call of its own and imports no icon set, so anything may ask it
 * whether a name is an icon.
 *
 * THE NAMES are the keys of src/config/iconFiles.json, the committed name ->
 * content-hash map scripts/build-icon-files.mjs writes beside the artwork
 * files (`public/icons/<NAME>.<hash>.json`). They are exactly the manifest's
 * (src/config/iconManifest.json): every icon a card may wear. A name is
 * checked as an OWN key, so `constructor` or `__proto__` is never an icon.
 *
 * THE CACHE holds one entry per name for the whole page: idle, loading, ready
 * (with its artwork) or failed. Every LibraryIcon drawing a name subscribes to
 * its entry, so one request serves them all, and a failed name is asked again
 * at most once a minute, by something new drawing it.
 *
 * PACKS (KPR-1188). The icon picker draws a page of icons at once, so it asks
 * for a category's PACK (config/iconPacks.js: every icon of the category in one
 * file) instead of one file per tile: `loadLibraryIconPage` plans a page's
 * requests and `loadLibraryIconPack` fetches a pack and fills the same cache,
 * so every other surface drawing one of its icons gets it free. A pack that
 * fails falls back to the per-icon files.
 */

const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

/** True when `name` is an icon of the library. */
export function isLibraryIconName(name) {
	return typeof name === 'string' && name !== '' && own(ICON_FILES, name);
}

/** Every library icon's name, sorted (the picker's catalog, Admin › Icons). */
export const LIBRARY_ICON_NAMES = Object.freeze(Object.keys(ICON_FILES).sort());

/** Where a library icon's artwork file is served; null for a name that is not one. */
export function libraryIconUrl(name) {
	if (!isLibraryIconName(name)) return null;
	return `${process.env.PUBLIC_URL || ''}/icons/${iconFileName(name, ICON_FILES[name])}`;
}

/** How long a failed name waits before something drawing it may ask again. */
export const RETRY_AFTER_MS = 60 * 1000;

const UNKNOWN = Object.freeze({ state: 'unknown', artwork: null });
const IDLE = Object.freeze({ state: 'idle', artwork: null });
const LOADING = Object.freeze({ state: 'loading', artwork: null });

let defaultLoader = null;
let loader = null;
let defaultPackLoader = null;
let packLoader = null;
let packs = new Map();
let resolver = null;
let entries = new Map();
let warned = new Set();

/** Says, once per name for the page, that an icon is drawn as the fallback and why. */
export function warnIconOnce(name, why) {
	const key = `${name}`;
	if (warned.has(key)) return;
	warned.add(key);
	// eslint-disable-next-line no-console
	console.warn(`[icons] ${why}; drawing the fallback glyph instead.`);
}

function entryOf(name) {
	let entry = entries.get(name);
	if (!entry) {
		entry = { snap: IDLE, listeners: new Set(), failedAt: 0 };
		entries.set(name, entry);
	}
	return entry;
}

function settle(entry, snap) {
	entry.snap = snap;
	entry.listeners.forEach(listener => listener());
}

const failed = reason => Object.freeze({ state: 'failed', artwork: null, reason });

function accept(name, artwork) {
	const problem = checkArtwork(artwork);
	if (problem) throw new Error(`the file for ${name} is not an icon file (${problem})`);
	return Object.freeze({ state: 'ready', artwork });
}

/**
 * The icon's state now: `{ state: 'unknown' | 'idle' | 'loading' | 'ready' | 'failed', artwork }`.
 * The same object until the state changes (useSyncExternalStore's snapshot).
 */
export function libraryIconSnapshot(name) {
	if (!isLibraryIconName(name)) return UNKNOWN;
	const entry = entryOf(name);
	if (entry.snap === IDLE && resolver) {
		// A synchronous resolver (the tests): answered once, then held like a fetch.
		try {
			const artwork = resolver(name);
			entry.snap = artwork ? accept(name, artwork) : failed(`no artwork for ${name}`);
		} catch (err) {
			entry.snap = failed(err && err.message);
		}
	}
	return entry.snap;
}

/** Calls `listener` whenever the icon's state changes; returns the unsubscribe. */
export function subscribeLibraryIcon(name, listener) {
	if (!isLibraryIconName(name)) return () => {};
	const entry = entryOf(name);
	entry.listeners.add(listener);
	return () => { entry.listeners.delete(listener); };
}

/** Asks for an icon's file unless it is held, on its way, or failed under a minute ago. */
export function requestLibraryIcon(name) {
	if (!isLibraryIconName(name)) return;
	const entry = entryOf(name);
	const { state } = libraryIconSnapshot(name);
	if (state === 'ready' || state === 'loading') return;
	if (state === 'failed' && (resolver || Date.now() - entry.failedAt < RETRY_AFTER_MS)) return;
	const load = loader || defaultLoader;
	if (!load) return;
	settle(entry, LOADING);
	let pending;
	try {
		pending = Promise.resolve(load(name, libraryIconUrl(name)));
	} catch (err) {
		pending = Promise.reject(err);
	}
	pending
		.then((artwork) => {
			if (entries.get(name) !== entry) return;
			settle(entry, accept(name, artwork));
		})
		.catch((err) => {
			if (entries.get(name) !== entry) return;
			entry.failedAt = Date.now();
			settle(entry, failed(err && err.message));
		});
}

/** Starts fetching icons ahead of drawing them (a picker's next page). Names that are not icons are skipped. */
export function prefetchLibraryIcons(names) {
	(Array.isArray(names) ? names : []).forEach(name => requestLibraryIcon(name));
}

/** LibraryIcon installs the loader that fetches a file, `(name, url) => Promise<artwork>`. */
export function installDefaultLibraryIconLoader(fn) {
	defaultLoader = typeof fn === 'function' ? fn : null;
}

/** Tests: replace how a file is fetched, `(name, url) => Promise<artwork>`; null puts LibraryIcon's back. */
export function setLibraryIconLoader(fn) {
	loader = typeof fn === 'function' ? fn : null;
}

/** Tests: a synchronous `name => artwork | null` answered before any fetch (src/setupTests.js); null removes it. */
export function setLibraryIconResolver(fn) {
	resolver = typeof fn === 'function' ? fn : null;
}

/** Tests: forget every icon held, on its way or warned about. */
export function resetLibraryIcons() {
	entries = new Map();
	warned = new Set();
	packs = new Map();
}

/** LibraryIcon installs the loader that fetches a pack, `(id, url) => Promise<pack>`. */
export function installDefaultLibraryIconPackLoader(fn) {
	defaultPackLoader = typeof fn === 'function' ? fn : null;
}

/** Tests: replace how a pack is fetched, `(id, url) => Promise<pack>`; null puts LibraryIcon's back. */
export function setLibraryIconPackLoader(fn) {
	packLoader = typeof fn === 'function' ? fn : null;
}

// Whether something may ask for this name now: not held, not on its way, and
// not failed within the last minute.
function askable(name) {
	const { state } = libraryIconSnapshot(name);
	if (state === 'ready' || state === 'loading' || state === 'unknown') return false;
	if (state === 'failed') return Date.now() - entryOf(name).failedAt >= RETRY_AFTER_MS;
	return true;
}

/**
 * Fetches one pack and settles its icons in the cache. `pack` is
 * `{ id, url, names }`: the names are the icons the caller wants from it (they
 * show as loading meanwhile); every icon the pack holds is cached, each checked
 * on its own, so one bad member fails only itself. A name the pack does not
 * hold fails with a reason. If the pack cannot be had (offline, a non-200, the
 * host's app shell after a deploy, a body that is not a pack) its names fall
 * back to their own per-icon requests. One request per pack at a time; a pack
 * that failed is asked again after a minute. Resolves true when the pack was
 * had (or needs no request), false when it was not. Never rejects.
 */
export function loadLibraryIconPack({ id, url, names }) {
	if (resolver) return Promise.resolve(true);
	const wanted = (Array.isArray(names) ? names : []).filter(askable);
	const held = packs.get(id);
	if (held && held.promise) {
		// Already on its way: these names ride along (they show as loading, and
		// fall back with the rest if the pack fails).
		wanted.forEach((name) => { held.names.add(name); settle(entryOf(name), LOADING); });
		return held.promise;
	}
	if (held && Date.now() - held.failedAt < RETRY_AFTER_MS) {
		wanted.forEach(requestLibraryIcon);
		return Promise.resolve(false);
	}
	if (!wanted.length) return Promise.resolve(true);
	const load = packLoader || defaultPackLoader;
	if (!load) {
		wanted.forEach(requestLibraryIcon);
		return Promise.resolve(false);
	}
	wanted.forEach(name => settle(entryOf(name), LOADING));
	const record = { promise: null, failedAt: 0, names: new Set(wanted) };
	packs.set(id, record);
	let pending;
	try {
		pending = Promise.resolve(load(id, url));
	} catch (err) {
		pending = Promise.reject(err);
	}
	record.promise = pending
		.then((pack) => {
			if (packs.get(id) !== record) return false;
			const problem = checkPack(pack);
			if (problem) throw new Error(`the pack ${id} is not a pack file (${problem})`);
			for (const name of Object.keys(pack.icons)) {
				if (!isLibraryIconName(name)) continue;
				const entry = entryOf(name);
				if (entry.snap.state === 'ready') continue;
				try {
					settle(entry, accept(name, { v: ARTWORK_VERSION, ...pack.icons[name] }));
				} catch (err) {
					entry.failedAt = Date.now();
					settle(entry, failed(err && err.message));
				}
			}
			record.names.forEach((name) => {
				const entry = entryOf(name);
				if (entry.snap.state !== 'loading') return;
				entry.failedAt = Date.now();
				settle(entry, failed(`${name} is not in the pack ${id}`));
			});
			return true;
		})
		.catch(() => {
			if (packs.get(id) !== record) return false;
			record.promise = null;
			record.failedAt = Date.now();
			record.names.forEach((name) => {
				const entry = entryOf(name);
				if (entry.snap.state !== 'loading') return;
				settle(entry, IDLE);
				requestLibraryIcon(name);
			});
			return false;
		});
	return record.promise;
}

/**
 * Starts the requests a picker page needs, and none for icons already held or
 * on their way, so a page seen before asks for nothing. `packOf(name)` says
 * which pack holds an icon (`{ id, url, names }`-able: `{ id, url }`; null for
 * an icon in none). Browsing (no search) asks for the pack of every category
 * the page touches: one request for a page of a category. A search whose hits
 * scatter asks for a pack only where PACK_MIN_NAMES or more of the page's
 * icons are in it, and fetches the rest as their own small files.
 */
export function loadLibraryIconPage(names, packOf, { browsing = false } = {}) {
	if (resolver) return;
	const groups = new Map();
	const singles = [];
	for (const name of new Set(Array.isArray(names) ? names : [])) {
		if (!askable(name)) continue;
		const pack = typeof packOf === 'function' ? packOf(name) : null;
		if (!pack) {
			singles.push(name);
		} else {
			if (!groups.has(pack.id)) groups.set(pack.id, { pack, names: [] });
			groups.get(pack.id).names.push(name);
		}
	}
	for (const { pack, names: inPack } of groups.values()) {
		if (browsing || inPack.length >= PACK_MIN_NAMES) loadLibraryIconPack({ ...pack, names: inPack });
		else singles.push(...inPack);
	}
	singles.forEach(requestLibraryIcon);
}
